// not-graded-games.js -- #1655 B3b A1, the pure halves of the not-graded games fetch.
// Single job: say which game ids a NOT GRADED bet needs that the realtime window lacks, and
// merge the fetched rows into the status consumers' list. Reads its arguments; writes nothing;
// no React, no fetch. The fetch itself is src/hooks/useNotGradedGames.js.
import { gradingLeague, isNotGradedLeague, isUntrackedLeg, notGradedLegGameId } from "./betting";

/**
 * notGradedMissingIds -- the game ids a not-graded bet needs that the window list lacks.
 *
 * Single job: for every unsettled bet, every tracked leg whose gradingLeague is not-graded
 * and whose game (notGradedLegGameId) is absent from `games`, collect String(id);
 * return them de-duplicated and sorted. Pure.
 */
export function notGradedMissingIds(bets, games) {
  const have = new Set((Array.isArray(games) ? games : []).filter(Boolean).map((g) => String(g.id)));
  const ids = new Set();
  for (const bet of Array.isArray(bets) ? bets : []) {
    if (!bet || bet.settledAt || bet.finalResult) continue;
    for (const leg of bet.legs || []) {
      if (!leg || isUntrackedLeg(leg)) continue;
      const gid = notGradedLegGameId(bet, leg);
      if (!gid || have.has(String(gid))) continue;
      if (!isNotGradedLeague(gradingLeague(bet, leg, null))) continue;
      ids.add(String(gid));
    }
  }
  return [...ids].sort();
}

/**
 * mergeNotGradedGames -- the status consumers' game list: the window plus the extra rows.
 *
 * Single job: append `extras` whose id the window does not hold (the window row wins on a
 * collision); with no extras, return `games` itself so its identity (and every memo keyed on
 * it) is unchanged. Pure.
 */
export function mergeNotGradedGames(games, extras) {
  if (!Array.isArray(extras) || extras.length === 0) return games;
  const list = Array.isArray(games) ? games : [];
  const have = new Set(list.filter(Boolean).map((g) => String(g.id)));
  const add = extras.filter((g) => g && !have.has(String(g.id)));
  return add.length === 0 ? games : [...list, ...add];
}
